import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { API_PATHS } from './api-paths';

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  primeiroAcesso: boolean;
  perfil: 'ADMIN' | 'PARCEIRO';
  usuarioUuid: string;
  parceiroUuid: string | null;
  nome: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly key = 'cz_backoffice_session';

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  get session(): LoginResponse | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      return JSON.parse(sessionStorage.getItem(this.key) || 'null') as LoginResponse | null;
    } catch {
      return null;
    }
  }

  get token(): string | null {
    return this.session?.accessToken ?? null;
  }

  get nomeUsuario(): string {
    const nome = this.session?.nome?.trim();

    if (nome) {
      return nome;
    }

    return this.perfil === 'ADMIN' ? 'Administrador' : 'Parceiro';
  }

  get perfil(): 'ADMIN' | 'PARCEIRO' | null {
    return this.session?.perfil ?? null;
  }

  login(email: string, senha: string) {
    return this.http
      .post<LoginResponse>(environment.apiUrl + API_PATHS.login, { email, senha })
      .pipe(
        tap((resposta) => {
          if (this.isBrowser) {
            sessionStorage.setItem(this.key, JSON.stringify(resposta));
          }
        }),
      );
  }

  trocarSenha(senhaAtual: string, novaSenha: string) {
    return this.http.post<void>(environment.apiUrl + API_PATHS.trocarSenha, {
      senhaAtual,
      novaSenha,
    });
  }

  redirectAfterLogin(): Promise<boolean> {
    const sessao = this.session;

    if (!sessao) {
      return this.router.navigateByUrl('/login');
    }

    // Obriga a troca de senha no primeiro acesso,
    // tanto para ADMIN quanto para PARCEIRO.
    if (sessao.primeiroAcesso === true) {
      return this.router.navigateByUrl('/primeiro-acesso');
    }

    if (sessao.perfil === 'ADMIN') {
      return this.router.navigateByUrl('/admin/dashboard');
    }

    if (sessao.perfil === 'PARCEIRO') {
      return this.router.navigateByUrl('/parceiro/dashboard');
    }

    return this.router.navigateByUrl('/login');
  }

  solicitarPrimeiroAcesso(email: string) {
    return this.http.post<void>(`${environment.apiUrl}/auth/primeiro-acesso/solicitar`, {
      email,
    });
  }

  confirmarPrimeiroAcesso(token: string, novaSenha: string) {
    return this.http.post<void>(`${environment.apiUrl}/auth/primeiro-acesso/confirmar`, {
      token,
      novaSenha,
    });
  }

  concluirPrimeiroAcesso(
    email: string,
    novaSenha: string
  ) {
    return this.http.post<{ mensagem: string }>(
      `${environment.apiUrl}/auth/primeiro-acesso/concluir`,
      {
        email,
        novaSenha
      }
    );
  }

  logout(): void {
    if (this.isBrowser) {
      sessionStorage.removeItem(this.key);
    }

    void this.router.navigateByUrl('/login');
  }
}
